import { useState, useCallback, useMemo, useEffect } from "react";
import { Comment, CommentStat, Reply, getEmail, ManageUser } from "@induz/sdk";
import { useToast } from "./Toast";
import Email from "components/Settings/Email/index";

/**
 * Custom hook to manage PDF comments
 * Handles loading, saving, deleting, and parsing comments from backend
 * @param {string} fileName - The name of the PDF file
 * @param {string[]} dupCurrentPath - Array of path segments for the file location
 * @param {boolean} isVisible - Whether the PDF viewer is visible
 * @param {boolean} isSlidesMode - Whether slides mode is active
 * @param {Object} fo - Whether slides mode is active
 * @return {Object} Object containing comment state and functions
 * @return {Array} return.comments - Array of parsed comment objects
 * @return {Function} return.setComments - Function to update comments state
 * @return {boolean} return.isCommentMode - Whether comment mode is active
 * @return {Function} return.setIsCommentMode - Function to toggle comment mode
 * @return {Object|null} return.pendingComment - Pending comment being created
 * @return {Function} return.setPendingComment - Function to set pending comment
 * @return {string} return.commentText - Text content of the comment being created
 * @return {Function} return.setCommentText - Function to update comment text
 * @return {Function} return.loadComments - Function to load comments from backend
 * @return {Function} return.saveComment - Function to save a comment to backend
 * @return {Function} return.deleteComment - Function to delete a comment from backend
 * @return {Function} return.getCurrentPageComments - Function to get comments for a specific page
 */

const MAX_COMMENT_SIZE = 256;
export const useComments = (fileName, dupCurrentPath, isVisible, isSlidesMode, fo) => {
  const showToast = useToast();
  const [comments, setCommentsState] = useState([]);
  const [validUserEmails, setValidUserEmails] = useState(new Set());
  const [lastUpdated, setLastUpdated] = useState(0); // Store last updated timestamp in milliseconds (0 for initial load)
  const [commentLength, setCommentLength] = useState(0);

  // Utility to sort comments by page, then y (top to bottom), then x (left to right)
  const sortComments = useCallback((commentsList) => {
    return [...commentsList].sort((a, b) => {
      // First sort by page number
      if (a.page !== b.page) return a.page - b.page;

      // Then sort by y-coordinate (top to bottom)
      // We use a small threshold (0.01 = 1% of page height) to treat items on the same "line"
      const yDiff = a.y - b.y;
      if (Math.abs(yDiff) > 0.01) {
        return yDiff;
      }

      // Finally sort by x-coordinate (left to right)
      return a.x - b.x;
    });
  }, []);


  const MENTION_EMAIL_REGEX = /@([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

  const extractMentionedEmails = useCallback((text) => {
    if (!text) return [];

    const emails = new Set();
    let match;

    while ((match = MENTION_EMAIL_REGEX.exec(text)) !== null) {
      const email = match[1].toLowerCase();
      // Only add email if it exists in the valid user list
      if (validUserEmails.has(email)) {
        emails.add(email);
      }
    }

    return Array.from(emails);
  }, [validUserEmails]);


  const setComments = useCallback((newCommentsOrFn) => {
    setCommentsState((prev) => {
      const next = typeof newCommentsOrFn === "function" ? newCommentsOrFn(prev) : newCommentsOrFn;
      return sortComments(next);
    });
  }, [sortComments]);

  const [isCommentMode, setIsCommentMode] = useState(false);
  const [pendingComment, setPendingComment] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [replyingToCommentId, setReplyingToCommentId] = useState(null);
  const [replyText, setReplyText] = useState("");

  // Construct parentPath from dupCurrentPath
  const getParentPath = useCallback(() => {
    return dupCurrentPath?.length === 0 ? "/" : `/${dupCurrentPath.join("/")}`;
  }, [dupCurrentPath]);

  // Parse backend Comment objects to UI format: { id, page, x, y, text, createdAt }
  const parseBackendComments = useCallback((backendComments) => {
    if (!Array.isArray(backendComments)) return [];

    return backendComments.map((backendComment) => {
      try {
        // Extract commentId and convert to string ID
        const commentId = backendComment?.commentId ||
          (typeof backendComment?.getCommentId === "function" ? backendComment.getCommentId() : undefined);

        // Convert Uint8Array to string ID
        let id;
        if (commentId instanceof Uint8Array) {
          id = Array.from(commentId).map(b => b.toString(16).padStart(2, "0")).join("");
        } else if (commentId) {
          id = String(commentId);
        } else {
          id = `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }

        // Get CommentStat object
        const commentStat = backendComment?.comment ||
          (typeof backendComment?.getCommentInfo === "function" ? backendComment.getCommentInfo() : undefined);

        // Extract text/content from CommentStat
        const text = commentStat?.content ||
          (typeof commentStat?.getContent === "function" ? commentStat.getContent() : "") ||
          "";

        // Extract email from CommentStat
        const email = commentStat?.email ||
          (typeof commentStat?.getEmail === "function" ? commentStat.getEmail() : "") ||
          "";

        // Extract replies from CommentStat
        const replies = commentStat?.replies ||
          (typeof commentStat?.getReplies === "function" ? commentStat.getReplies() : []) ||
          [];

        // Parse replies to UI format
        const parsedReplies = replies.map((reply) => {
          try {
            const replyEmail = reply?.email ||
              (typeof reply?.getEmail === "function" ? reply.getEmail() : "") ||
              "";
            const replyContent = reply?.content ||
              (typeof reply?.getContent === "function" ? reply.getContent() : "") ||
              "";
            const replyCreateTime = reply?.createdTime ||
              (typeof reply?.getCreatedTime === "function" ? reply.getCreatedTime() : undefined);

            let replyCreatedAt;
            if (replyCreateTime) {
              const timestamp = typeof replyCreateTime === "object" && replyCreateTime?.toNumber
                ? replyCreateTime.toNumber()
                : Number(replyCreateTime);
              replyCreatedAt = new Date(timestamp).toISOString();
            } else {
              replyCreatedAt = new Date().toISOString();
            }

            return {
              email: String(replyEmail),
              text: String(replyContent),
              createdAt: replyCreatedAt,
            };
          } catch (err) {
            console.warn("Failed to parse reply", err, reply);
            return {
              email: "",
              text: "",
              createdAt: new Date().toISOString(),
            };
          }
        });

        // Extract createTime and convert to ISO string
        const createTime = commentStat?.createTime ||
          (typeof commentStat?.getCreateTime === "function" ? commentStat.getCreateTime() : undefined);

        let createdAt;
        if (createTime) {
          const timestamp = typeof createTime === "object" && createTime?.toNumber
            ? createTime.toNumber()
            : Number(createTime);
          createdAt = new Date(timestamp).toISOString();
        } else {
          createdAt = new Date().toISOString();
        }

        // Parse location string: "page:1 x:0.6624 y:0.5252"
        const location = backendComment?.location ||
          (typeof backendComment?.getLocation === "function" ? backendComment.getLocation() : "");

        let page = 1;
        let x = 0;
        let y = 0;
        let width = 0;
        let height = 0;

        if (typeof location === "string" && location) {
          const pageMatch = location.match(/page\s*:\s*(\d+)/i);
          if (pageMatch) {
            page = parseInt(pageMatch[1], 10);
          }

          const xMatch = location.match(/x\s*:\s*([0-9.]+)/i);
          if (xMatch) {
            x = parseFloat(xMatch[1]);
          }

          const yMatch = location.match(/y\s*:\s*([0-9.]+)/i);
          if (yMatch) {
            y = parseFloat(yMatch[1]);
          }

          const wMatch = location.match(/w\s*:\s*([0-9.]+)/i);
          if (wMatch) {
            width = parseFloat(wMatch[1]);
          }

          const hMatch = location.match(/h\s*:\s*([0-9.]+)/i);
          if (hMatch) {
            height = parseFloat(hMatch[1]);
          }
        }

        // Extract state from backend comment
        const state = backendComment?.state ||
          (typeof backendComment?.getState === "function" ? backendComment.getState() : "active") ||
          "active";

        return {
          id,
          page: Number.isFinite(page) ? page : 1,
          x: Number.isFinite(x) ? x : 0,
          y: Number.isFinite(y) ? y : 0,
          width: Number.isFinite(width) ? width : 0,
          height: Number.isFinite(height) ? height : 0,
          text: String(text),
          email: String(email),
          replies: parsedReplies,
          createdAt,
          state: String(state),
          _backendComment: backendComment,
        };
      } catch (itemErr) {
        console.warn("Failed to parse backend comment", itemErr, backendComment);
        return {
          id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          page: 1,
          x: 0,
          y: 0,
          text: "",
          email: "",
          replies: [],
          createdAt: new Date().toISOString(),
          state: "active",
          _backendComment: backendComment,
        };
      }
    });
  }, []);

  // Load comments from backend
  const loadComments = useCallback(async () => {
    if (!fileName) return { status: "same", comments: [] };

    try {
      const parentPath = getParentPath();
      const comment = new Comment();
      comment.setParentPath(parentPath);
      if (dupCurrentPath.length === 1 && (dupCurrentPath[0] === "shared" || dupCurrentPath[0] === "external_shared")) {
        fileName = fo.getFileUuId();
      }
      comment.setFileName(fileName);
      const [fetchedComments, status] = await comment.listComments(lastUpdated, commentLength);
      setCommentLength(fetchedComments.length);

      // Ensure we have valid status
      const responseStatus = String(status || "changed").toLowerCase();

      let parsedComments = [];

      if (responseStatus === "changed") {
        const commentsArray = Array.isArray(fetchedComments) ? fetchedComments : [];
        parsedComments = parseBackendComments(commentsArray);
        // Update lastUpdated timestamp when comments changed
        setLastUpdated(Date.now());
      }

      return {
        status: responseStatus,
        comments: parsedComments
      };
    } catch (e) {
      console.warn("Failed to load comments", e);
      // showToast("Failed to load comments", "error");
      return { status: "same", comments: [] };
    }
  }, [fileName, getParentPath, parseBackendComments, lastUpdated, dupCurrentPath, fo]);

  // Save comment to backend
  const saveComment = useCallback(async (pendingComment, commentText) => {
    if (!pendingComment || !commentText.trim() || !fileName) return null;

    try {
      if (commentText.length > MAX_COMMENT_SIZE) {
        showToast(`Comment exceeds maximum length of ${MAX_COMMENT_SIZE} characters`, "error");
        return null;
      }
      const parentPath = getParentPath();
      const comment = new Comment();
      const stat = new CommentStat();

      comment.setParentPath(parentPath);
      if (dupCurrentPath.length === 1 && (dupCurrentPath[0] === "shared" || dupCurrentPath[0] === "external_shared")) {
        fileName = fo.getFileUuId();
      }
      comment.setFileName(fileName);
      const location = `page:${pendingComment.page} x:${pendingComment.x.toFixed(4)} y:${pendingComment.y.toFixed(4)} w:${(pendingComment.width || 0).toFixed(4)} h:${(pendingComment.height || 0).toFixed(4)}`;
      comment.setLocation(location);
      comment.setState("active");

      stat.setEmail(getEmail());
      stat.setContent(commentText.trim());
      const mentionedEmails = extractMentionedEmails(commentText);
      stat.setMentionedEmails(mentionedEmails);
      const time = Date.now();
      stat.setCreateTime(time);
      stat.setModifiedTime(time);

      comment.setComment(stat);
      await comment.addComment();
      showToast("Comment created successfully", "success");

      const commentId = comment.getCommentId();

      // Convert Uint8Array to string ID if needed
      let id;
      if (commentId instanceof Uint8Array) {
        id = Array.from(commentId).map(b => b.toString(16).padStart(2, "0")).join("");
      } else if (commentId) {
        id = String(commentId);
      } else {
        id = `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }

      return {
        id,
        page: pendingComment.page,
        x: pendingComment.x,
        y: pendingComment.y,
        width: pendingComment.width || 0,
        height: pendingComment.height || 0,
        text: commentText.trim(),
        email: getEmail(),
        createdAt: new Date().toISOString(),
      };
    } catch (error) {
      console.error("failed to create a comment", error);
      showToast("Failed to create a comment", "error");
      throw error;
    }
  }, [fileName, getParentPath, extractMentionedEmails]);

  // Add reply to a comment
  const addReply = useCallback(async (commentId, replyText) => {
    if (!commentId || !replyText.trim() || !fileName) return;

    try {
      if (replyText.length > MAX_COMMENT_SIZE) {
        showToast(`Reply exceeds maximum length of ${MAX_COMMENT_SIZE} characters`, "error");
        return;
      }
      let commentIdBytes;
      if (commentId instanceof Uint8Array) {
        commentIdBytes = commentId;
      } else if (typeof commentId === "string") {
        try {
          const hexPairs = commentId.match(/.{1,2}/g) || [];
          commentIdBytes = new Uint8Array(hexPairs.map(byte => parseInt(byte, 16)));
        } catch {
          throw new Error("Invalid comment ID format");
        }
      } else {
        throw new Error("Invalid comment ID format");
      }
      const updateType = "ADD_REPLY";
      const comment = new Comment();
      const parentPath = getParentPath();
      comment.setParentPath(parentPath);
      if (dupCurrentPath.length === 1 && (dupCurrentPath[0] === "shared" || dupCurrentPath[0] === "external_shared")) {
        fileName = fo.getFileUuId();
      }
      comment.setFileName(fileName);
      comment.setCommentId(commentIdBytes);
      const time = Date.now();
      const email = getEmail();

      const reply = new Reply(email, replyText, time, time);

      const getComment = await comment.getComment();
      getComment.setCommentId(commentIdBytes);

      // Extract mentions from the reply and merge with existing mentions
      const replyMentions = extractMentionedEmails(replyText);
      if (replyMentions.length > 0) {
        const commentStat = getComment.getCommentInfo();
        const existingMentions = commentStat.getMentionedEmails() || [];
        const mergedMentions = Array.from(new Set([...existingMentions, ...replyMentions]));
        commentStat.setMentionedEmails(mergedMentions);
      }

      await getComment.addReply(reply);
      await getComment.updateComment(updateType);
      showToast("Reply added successfully", "success");

      const newReply = {
        email: getEmail(),
        text: replyText.trim(),
        createdAt: new Date().toISOString(),
      };

      // Update comments in local state
      setComments((prevComments) =>
        prevComments.map((comment) => {
          if (comment.id === commentId) {
            return {
              ...comment,
              replies: [...(comment.replies || []), newReply],
            };
          }
          return comment;
        })
      );

      setReplyingToCommentId(null);
      setReplyText("");

    } catch (error) {
      console.error("Failed to add reply:", error);
      showToast("Failed to add reply", "error");
      throw error;
    }
  }, [fileName, getParentPath, setComments, extractMentionedEmails]);

  // Update comment content or state (or both)
  const updateComment = useCallback(async (commentId, updatedText = null, newState = null) => {
    if (!commentId || !fileName) return;
    // At least one of text or state must be provided
    if (!updatedText && !newState) return;
    if (updatedText && updatedText.length > MAX_COMMENT_SIZE) {
      showToast(`Comment exceeds maximum length of ${MAX_COMMENT_SIZE} characters`, "error");
      return;
    }
    try {
      let commentIdBytes;
      if (commentId instanceof Uint8Array) {
        commentIdBytes = commentId;
      } else if (typeof commentId === "string") {
        try {
          const hexPairs = commentId.match(/.{1,2}/g) || [];
          commentIdBytes = new Uint8Array(hexPairs.map(byte => parseInt(byte, 16)));
        } catch {
          throw new Error("Invalid comment ID format");
        }
      } else {
        throw new Error("Invalid comment ID format");
      }

      let comment = new Comment();
      const parentPath = getParentPath();
      comment.setParentPath(parentPath);
      if (dupCurrentPath.length === 1 && (dupCurrentPath[0] === "shared" || dupCurrentPath[0] === "external_shared")) {
        fileName = fo.getFileUuId();
      }
      comment.setFileName(fileName);
      comment.setCommentId(commentIdBytes);
      const getComment = await comment.getComment();

      comment = getComment;

      // If updating text, check ownership and update content
      if (updatedText && updatedText.trim()) {
        if (comment.comment.getEmail() != getEmail()) {
          throw new Error("Only author can edit the comment");
        }
        const commentStat = comment.getCommentInfo();
        commentStat.setContent(updatedText.trim());
        // Update mentions for the edited comment text
        const updatedMentions = extractMentionedEmails(updatedText);
        commentStat.setMentionedEmails(updatedMentions);
      }

      // If updating state, set the new state
      let isResolved = false;
      let isReOpen = false;
      if (newState) {
        if (newState == "active") {
          isReOpen = true;
        } else if (newState === "resolved") {
          isResolved = true;
        }
      }
      let updateType;
      if (updatedText && newState) {
        updateType = "UPDATE_COMMENT_TEXT";
      } else if (updatedText) {
        updateType = "UPDATE_COMMENT_TEXT";
      } else if (newState) {
        updateType = "UPDATE_STATUS";
      }

      comment.setCommentId(commentIdBytes);
      await comment.updateComment(updateType, undefined, isResolved, isReOpen);
      if (updatedText && newState) {
        showToast("Comment updated successfully", "success");
      } else if (updatedText) {
        showToast("Comment updated successfully", "success");
      } else if (newState && newState === "resolved") {
        showToast("Comment resolved successfully", "success");
      } else if (newState && newState === "active") {
        showToast("Comment re-opened successfully", "success");
      }

      // Update local state
      setComments((prevComments) =>
        prevComments.map((c) => {
          if (c.id === commentId) {
            const updated = { ...c };
            if (updatedText && updatedText.trim()) {
              updated.text = updatedText.trim();
            }
            if (newState) {
              updated.state = newState;
            }
            return updated;
          }
          return c;
        })
      );

    } catch (error) {
      console.error("Failed to update comment:", error);
      showToast("Failed to update comment", "error");
      throw error;
    }
  }, [fileName, getParentPath, setComments, dupCurrentPath, fo, extractMentionedEmails]);

  // Delete comment from backend
  const deleteComment = useCallback(async (commentId) => {
    if (!commentId || !fileName) return;

    try {
      const parentPath = getParentPath();
      const comment = new Comment();
      comment.setParentPath(parentPath);
      if (dupCurrentPath.length === 1 && (dupCurrentPath[0] === "shared" || dupCurrentPath[0] === "external_shared")) {
        fileName = fo.getFileUuId();
      }
      comment.setFileName(fileName);

      let commentIdBytes;
      if (commentId instanceof Uint8Array) {
        commentIdBytes = commentId;
      } else if (typeof commentId === "string") {
        try {
          const hexPairs = commentId.match(/.{1,2}/g) || [];
          commentIdBytes = new Uint8Array(hexPairs.map(byte => parseInt(byte, 16)));
        } catch {
          throw new Error("Invalid comment ID format");
        }
      } else {
        throw new Error("Invalid comment ID format");
      }

      comment.setCommentId(commentIdBytes);
      await comment.removeComment();
      showToast("Comment deleted successfully", "success");
    } catch (error) {
      console.error("Failed to delete comment:", error);
      showToast("Failed to delete comment", "error");
      throw error;
    }
  }, [fileName, getParentPath]);

  // Fetch valid users for email filtering
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userList = await ManageUser.listUsers();
        if (Array.isArray(userList)) {
          const emailSet = new Set(userList.map(user => user.email.toLowerCase()));
          setValidUserEmails(emailSet);
        }
      } catch (err) {
        console.error("Failed to fetch users for email filtering", err);
      }
    };
    fetchUsers();
  }, []);

  // Disable comment mode when viewer hides or slides mode is active
  useEffect(() => {
    if (!isVisible || isSlidesMode) {
      setIsCommentMode(false);
    }
  }, [isVisible, isSlidesMode]);

  // Update reply content
  const updateReply = useCallback(async (commentId, replyIndex, updatedText) => {
    if (!commentId || !fileName) return;
    if (updatedText.length > MAX_COMMENT_SIZE) {
      showToast(`Reply exceeds maximum length of ${MAX_COMMENT_SIZE} characters`, "error");
      return;
    }

    try {
      let commentIdBytes;
      if (commentId instanceof Uint8Array) {
        commentIdBytes = commentId;
      } else if (typeof commentId === "string") {
        try {
          const hexPairs = commentId.match(/.{1,2}/g) || [];
          commentIdBytes = new Uint8Array(hexPairs.map(byte => parseInt(byte, 16)));
        } catch {
          throw new Error("Invalid comment ID format");
        }
      } else {
        throw new Error("Invalid comment ID format");
      }

      let comment = new Comment();
      const email = getEmail();
      const parentPath = getParentPath();
      comment.setParentPath(parentPath);
      if (dupCurrentPath.length === 1 && (dupCurrentPath[0] === "shared" || dupCurrentPath[0] === "external_shared")) {
        fileName = fo.getFileUuId();
      }
      comment.setFileName(fileName);
      comment.setCommentId(commentIdBytes);
      const getComment = await comment.getComment();
      comment = getComment;
      const updateType = "UPDATE_REPLY_TEXT";
      const numberOfReplies = (comment.comment && comment.comment.replies) ? comment.comment.replies.length : 0;
      if (replyIndex < 0 || replyIndex >= numberOfReplies) {
        throw new Error("Invalid reply index");
      }
      if (comment.comment.replies[replyIndex].getEmail() != email) {
        throw new Error("Ownly author can edit the reply");
      }
      comment.comment.replies[replyIndex].setContent(updatedText);
      comment.setCommentId(commentIdBytes);
      await comment.updateComment(updateType, replyIndex);
      showToast("Reply updated successfully", "success");

      // Update local state only after successful backend update
      setComments((prevComments) =>
        prevComments.map((c) => {
          if (c.id === commentId) {
            const newReplies = Array.isArray(c.replies) ? [...c.replies] : [];
            if (replyIndex >= 0 && replyIndex < newReplies.length) {
              newReplies[replyIndex] = {
                ...newReplies[replyIndex],
                text: updatedText.trim(),
              };
            }
            return {
              ...c,
              replies: newReplies,
            };
          }
          return c;
        })
      );
    } catch (error) {
      console.error("Failed to update reply:", error);
      showToast("Failed to update reply", "error");
      throw error;
    }
  }, [fileName, getParentPath, setComments, showToast]);

  // Delete reply from a comment
  const deleteReply = useCallback(async (commentId, replyIndex) => {
    try {
      if (!commentId || !fileName) return;

      const parentPath = getParentPath();
      let commentObj = new Comment();
      commentObj.setParentPath(parentPath);
      if (dupCurrentPath.length === 1 && (dupCurrentPath[0] === "shared" || dupCurrentPath[0] === "external_shared")) {
        fileName = fo.getFileUuId();
      }
      commentObj.setFileName(fileName);

      let commentIdBytes;
      if (commentId instanceof Uint8Array) {
        commentIdBytes = commentId;
      } else if (typeof commentId === "string") {
        try {
          const hexPairs = commentId.match(/.{1,2}/g) || [];
          commentIdBytes = new Uint8Array(hexPairs.map(byte => parseInt(byte, 16)));
        } catch {
          throw new Error("Invalid comment ID format");
        }
      } else {
        throw new Error("Invalid comment ID format");
      }

      commentObj.setCommentId(commentIdBytes);
      const email = getEmail();
      const updateType = "REMOVE_REPLY";
      const getComment = await commentObj.getComment();
      commentObj = getComment;

      const numberOfReplies = (commentObj.comment && commentObj.comment.replies) ? commentObj.comment.replies.length : 0;
      if (replyIndex < 0 || replyIndex >= numberOfReplies) {
        throw new Error("Invalid reply index");
      }
      // Remove the reply from backend structure and persist
      commentObj.setCommentId(commentIdBytes);
      await commentObj.updateComment(updateType, replyIndex);
      showToast("Reply deleted successfully", "success");
      commentObj.comment.replies.splice(replyIndex, 1);

      // Update local state to remove the reply only after successful backend update
      setComments((prevComments) =>
        prevComments.map((c) => {
          if (c.id === commentId) {
            const newReplies = Array.isArray(c.replies) ? [...c.replies] : [];
            if (replyIndex >= 0 && replyIndex < newReplies.length) {
              newReplies.splice(replyIndex, 1);
            }
            return {
              ...c,
              replies: newReplies,
            };
          }
          return c;
        })
      );
    } catch (error) {
      console.error("Failed to delete reply:", error);
      showToast("Failed to delete reply", "error");
      throw error;
    }
  }, [fileName, getParentPath, setComments, showToast, dupCurrentPath, fo]);

  // Filter comments for current page
  const getCurrentPageComments = useCallback((currentPage) => {
    return comments.filter(c => c.page === currentPage);
  }, [comments]);

  // Resolve comment (wrapper for updateComment to update state to resolved)
  const resolveComment = useCallback(async (commentId) => {
    return updateComment(commentId, null, "resolved");
  }, [updateComment]);

  return {
    // State
    comments,
    setComments,
    isCommentMode,
    setIsCommentMode,
    pendingComment,
    setPendingComment,
    commentText,
    setCommentText,
    replyingToCommentId,
    setReplyingToCommentId,
    replyText,
    setReplyText,
    // Functions
    loadComments,
    saveComment,
    updateComment,
    deleteComment,
    addReply,
    updateReply,
    deleteReply,
    getCurrentPageComments,
    resolveComment,
  };
};

